import { compose } from 'lodash/fp';
import React, {
  Dispatch,
  lazy,
  memo,
  Suspense,
  useEffect,
  useState,
} from 'react';
import { connect } from 'react-redux';
import {
  matchPath,
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  withRouter,
} from 'react-router-dom';

import { routesList } from '../containers/Routes';
import { DoGetApplications } from '../redux/apply/actions';
import { getApplication } from '../redux/apply/selectors';
import { DoSetRedirectUrl } from '../redux/session/actions';
import { getToken } from '../redux/session/selectors';
import { AppState } from '../redux/state';
import { Application } from '../services/apply';
import { CustomError, retryPromise } from '../utils/errors';

const Home = lazy(() =>
  retryPromise(() => import(/* webpackChunkName: "Home" */ './Home'))
);

const ReportsPage = lazy(() =>
  retryPromise(() =>
    import(/* webpackChunkName: "ReportsPage" */ './ReportsPage')
  )
);

const BuildingDetails = lazy(() =>
  retryPromise(() =>
    import(/* webpackChunkName: "BuildingDetails" */ './BuildingDetails')
  )
);

const BuildingUnits = lazy(() =>
  retryPromise(() =>
    import(/* webpackChunkName: "BuildingUnits" */ './BuildingUnits')
  )
);

const UnitDetails = lazy(() =>
  retryPromise(() =>
    import(/* webpackChunkName: "UnitDetails" */ './UnitDetails')
  )
);

const SearchUnits = lazy(() =>
  retryPromise(() =>
    import(/* webpackChunkName: "SearchUnits" */ './SearchUnits')
  )
);

const SuggestedBuildings = lazy(() =>
  retryPromise(() =>
    import(/* webpackChunkName: "SuggestedBuildings" */ './SuggestedBuildings')
  )
);

const SearchUnitsResults = lazy(() =>
  retryPromise(() =>
    import(/* webpackChunkName: "SearchUnitsResults" */ './SearchUnitsResults')
  )
);

const SearchBuildingsResults = lazy(() =>
  retryPromise(() =>
    import(
      /* webpackChunkName: "SearchBuildingsResults" */ './SearchBuildingsResults'
    )
  )
);

const RentalApplicationStart = lazy(() =>
  retryPromise(() =>
    import(
      /* webpackChunkName: "RentalApplicationStart" */ './RentalApplicationStart'
    )
  )
);

const LeaseApplicationContainer = lazy(() =>
  retryPromise(() =>
    import(
      /* webpackChunkName: "LeaseApplicationContainer" */ './LeaseApplicationContainer'
    )
  )
);

const ApplicationFeeIntro = lazy(() =>
  retryPromise(() =>
    import(
      /* webpackChunkName: "ApplicationFeeIntro" */ './ApplicationFeeIntro'
    )
  )
);

const SubmittedApplication = lazy(() =>
  retryPromise(() =>
    import(
      /* webpackChunkName: "SubmittedApplication" */ './SubmittedApplication'
    )
  )
);

const SignUp = lazy(() =>
  retryPromise(() => import(/* webpackChunkName: "SignUp" */ './SignUp'))
);

const PhoneVerification = lazy(() =>
  retryPromise(() =>
    import(/* webpackChunkName: "PhoneVerification" */ './PhoneVerification')
  )
);

const Fallback = (): React.ReactElement => {
  return <div>Loading Page...</div>;
};

const ErrorPage = (): React.ReactElement => {
  return (
    <div>There was an error with the application. Please try again later.</div>
  );
};

const Pages = memo(
  ({
    history,
    location,
    token,
    fetchApplication,
    application,
    setRedirectLocation,
  }: PagesProps): React.ReactElement => {
    // TODO make an AuthenticatedRoute component that abstracts this logic out
    const [loading, setLoading] = useState(false);
    const denyList = [routesList.LEASE_APPLICATION];
    const matchesDenyList = matchPath(location.pathname, {
      exact: false,
      path: denyList,
      strict: false,
    });

    const reloadApplication = async (unitId: number) => {
      setLoading(true);
      try {
        fetchApplication(unitId);
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (token && matchesDenyList && !application) {
        reloadApplication((matchesDenyList.params as any).unitId);
      }
    }, [token, location.pathname]);

    if (matchesDenyList && !token) {
      const atSignUp = matchPath(history.location.pathname, {
        exact: false,
        path: routesList.SIGN_UP,
        strict: false,
      });
      if (!atSignUp) setRedirectLocation(history.location);
      return <Redirect to={routesList.SIGN_UP} />;
    }

    const needsApplication = token && matchesDenyList && !application;

    try {
      return loading || needsApplication ? (
        <Fallback />
      ) : (
        <Suspense fallback={Fallback}>
          <Switch>
            <Redirect exact from="/" to={routesList.HOME} />
            <Route
              exact
              path={routesList.UNIT_DETAILS}
              component={UnitDetails}
            />
            <Route exact path={routesList.HOME} component={Home} />
            <Route exact path={routesList.SIGN_UP} component={SignUp} />
            <Route
              exact
              path={routesList.PHONE_VERIFICATION}
              component={PhoneVerification}
            />
            <Route
              exact
              path={routesList.BUILDING_UNITS}
              component={BuildingUnits}
            />
            <Route
              exact
              path={routesList.SEARCH_UNITS_RESULTS}
              component={SearchUnitsResults}
            />
            <Route
              exact
              path="/buildings/search/:criteria"
              component={SearchUnitsResults}
            />
            <Route
              exact
              path={routesList.SEARCH_BUILDINGS_RESULTS}
              component={SearchBuildingsResults}
            />
            <Route
              exact
              path={routesList.SUGGESTED_BUILDINGS}
              component={SuggestedBuildings}
            />
            <Route
              exact
              path={routesList.SEARCH_UNITS}
              component={SearchUnits}
            />
            <Route exact path="/buildings/search" component={SearchUnits} />
            <Route
              exact
              path={routesList.BUILDING_DETAILS}
              component={BuildingDetails}
            />
            <Route
              exact
              path={routesList.RENTAL_APPLICATION_START}
              component={RentalApplicationStart}
            />
            <Route path={routesList.REPORTS} component={ReportsPage} />
            <Route
              exact
              path={routesList.LEASE_APPLICATION}
              component={LeaseApplicationContainer}
            />
            <Route
              exact
              path={routesList.APPLICATION_FEE_INTRO}
              component={ApplicationFeeIntro}
            />
            <Route
              exact
              path={routesList.APPLICATION_SUBMITTED}
              component={SubmittedApplication}
            />

            <Route
              render={() => <Redirect exact from="/" to={routesList.HOME} />}
            />
          </Switch>
        </Suspense>
      );
    } catch (e) {
      return <ErrorPage />;
    }
  }
);

interface PagesProps extends RouteComponentProps {
  token: string;
  fetchApplication: (unitId: number) => void;
  application: Application;
  setRedirectLocation: typeof DoSetRedirectUrl;
}

const mapStateToProps = (state: AppState): any => ({
  application: getApplication(state),
  token: getToken(state),
});

const mapDispatchToProps = (dispatch: Dispatch<any>): any => ({
  fetchApplication: (unitId: number): Promise<Application[] | CustomError> =>
    DoGetApplications(unitId)(dispatch),
  setRedirectLocation: (location: any) =>
    DoSetRedirectUrl(location)(dispatch as any),
});

const enhancer = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter
);

export default enhancer(Pages);
